import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ContactResponse } from '../../../types/Contact';
import CustomAxios from '../../../utility/customAxios';
import { queryKeys } from '../queryKeys';

export type ContactFilterParam = {
  account?: string;
  event?: string;
  contact_role?: string[];
  contact_title?: string[];
};

export type ContactAvailableColumns =
  | 'full_name'
  | 'account_name'
  | 'account_code'
  | 'roles'
  | 'contact_title_value'
  | 'email'
  | 'phone_1'
  | 'phone_2'
  | 'tags';

export type ContactSortableColumns = Exclude<ContactAvailableColumns, 'roles' | 'contact_title_value'>;
export type ContactSortParam = {
  [K in ContactSortableColumns]?: -1 | 1;
};

type UseGetAllContactsParams = {
  page?: number;
  per_page?: number;
  search?: string;
  filters?: ContactFilterParam;
  sort?: ContactSortParam;
};

export type UseGetAllContactsProps = {
  params?: UseGetAllContactsParams;
  enabled?: boolean;
};

const fetchAllContacts = async ({ params }: UseGetAllContactsProps) => {
  const response = await CustomAxios.get<ContactResponse>(`contacts`, { params });
  const { data } = response;
  return data;
};

const fetchContactsForEvent = async ({ params }: UseGetAllContactsProps) => {
  if (!params?.filters?.event) return {};

  const response = await CustomAxios.get<ContactResponse>(`contacts/for-event`, { params });
  const { data } = response;
  return data;
};

function declareQuery(params: UseGetAllContactsParams | undefined, enabled: boolean, fetchFn: () => any) {
  const queryKey: any = [queryKeys.allContacts, params];
  return useQuery({
    enabled,
    queryKey,
    queryFn: fetchFn,
    placeholderData: keepPreviousData,
  });
}

export function useGetAllContacts({ params, enabled = true }: UseGetAllContactsProps) {
  return declareQuery(params, enabled, () => fetchAllContacts({ params }));
}

export function useGetContactsForEvent({ params, enabled = true }: UseGetAllContactsProps) {
  return declareQuery(params, enabled, () => fetchContactsForEvent({ params }));
}
